/* eslint-disable react-refresh/only-export-components -- arquivo mistura componente com hooks/types/helpers; refactor pra split é cosmético (HMR fast-refresh). */
import {
  createContext, useContext, useEffect, useRef, useState, useCallback,
  type ReactNode,
} from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { CurrentUserProfile, FeaturePermissionKey, Role } from '@/types'
import { loadOwnProfile, resolveUserName } from '@/features/auth/lib/load-profile'
import { captureFeatureError } from '@/lib/sentry-features'
import {
  PERSONAS, resolvePersonaRoleFromProfile,
  type Persona, type PersonaRole,
} from '@/features/auth/lib/personas'
import {
  IMPERS_LS_PERSONA_KEY,
} from '@/features/auth/lib/storage-keys'
import type { AccessState, ImpersonationAdminStash, ImpersonatingInfo } from '@/features/auth/lib/types'
import { useImpersonationRestore } from '@/features/auth/lib/use-impersonation-restore'
import { useGodModeMarker } from '@/features/auth/lib/use-god-mode-marker'
import { useImpersonationHandshake } from '@/features/auth/lib/use-impersonation-handshake'
import { signInWithGoogle as doSignInWithGoogle, signOutFully } from '@/features/auth/actions/sign-in'
import { startImpersonation as doStartImpersonation } from '@/features/auth/actions/start-impersonation'
import { endImpersonation as doEndImpersonation } from '@/features/auth/actions/end-impersonation'
import { enterAdminTenant as doEnterAdminTenant } from '@/features/auth/actions/enter-admin-tenant'

// Re-exports pra consumers existentes (ex: Sidebar importa PERSONAS, PersonaRole)
export { PERSONAS, type Persona, type PersonaRole }

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  persona: Persona
  profile: CurrentUserProfile | null
  realProfile: CurrentUserProfile | null
  access: AccessState
  displayName: string
  firstName: string
  effectiveRole: Role | null
  isRealAdmin: boolean
  isPlatformAdmin: boolean
  impersonating: ImpersonatingInfo | null
  hasFeature: (key: FeaturePermissionKey) => boolean
  setPersona: (role: PersonaRole) => void
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  refetchProfile: () => Promise<void>
  startImpersonation: (targetUserId: string) => Promise<void>
  endImpersonation: () => Promise<void>
  /**
   * God Mode: platform admin entra num tenant como si mesmo. Chama RPC no DB
   * (muda users.tenant_id do caller), dá refresh no JWT, abre app principal
   * em tab nova com ?tenant= pra marcar.
   */
  enterAdminTenant: (tenantId: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [profile, setProfile] = useState<CurrentUserProfile | null>(null)
  const [access, setAccess] = useState<AccessState>({ state: 'loading' })
  const [impersStash, setImpersStash] = useState<ImpersonationAdminStash | null>(null)
  const [persona, setPersonaState] = useState<Persona>(() => {
    const saved = localStorage.getItem(IMPERS_LS_PERSONA_KEY)
    return saved && PERSONAS[saved as PersonaRole] ? PERSONAS[saved as PersonaRole] : PERSONAS.coordinator
  })

  // Dedup: avoid concurrent loadProfile calls racing for the same user id.
  // onAuthStateChange can fire INITIAL_SESSION + TOKEN_REFRESHED + SIGNED_IN
  // in quick succession for the same user, each triggering a full profile
  // fetch. Ref-tracking the current load cancels obsolete results.
  const currentLoadIdRef = useRef<string | null>(null)

  const loadProfile = useCallback(async (u: User | null) => {
    if (!u) {
      currentLoadIdRef.current = null
      setProfile(null); setAccess({ state: 'anonymous' })
      return
    }
    const loadId = u.id
    currentLoadIdRef.current = loadId
    try {
      const p = await loadOwnProfile(u.id)
      if (currentLoadIdRef.current !== loadId) return // superseded
      if (!p) {
        setProfile(null)
        setAccess({ state: 'denied', reason: 'Perfil não registrado. Contate o admin.' })
        return
      }
      setProfile(p); setAccess({ state: 'authorized', profile: p })
    } catch (err) {
      console.error('[auth] loadProfile failed', err)
      // Falha aqui tranca o usuário fora do app ("Erro carregando perfil") —
      // precisa ser visível no Sentry, não só no console.
      captureFeatureError(err, 'auth', { stage: 'load_profile', user_id: u.id })
      if (currentLoadIdRef.current !== loadId) return
      setProfile(null)
      setAccess({ state: 'denied', reason: 'Erro carregando perfil.' })
    }
  }, [])

  // Sincroniza persona com o perfil real autenticado. Em auth real, o dashboard
  // precisa vir do role do profile: senão gestor/cliente caem no
  // CoordinatorDashboard por default do localStorage. Impersonação segue
  // o target (profile já é do user impersonado); god mode cai como 'owner'.
  useEffect(() => {
    if (!profile) return
    // Sincronização legítima: persona deriva de profile (auth). Não cabe derivar no render.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPersonaState(PERSONAS[resolvePersonaRoleFromProfile(profile)])
  }, [profile])

  // useEffects extraídos pra módulos próprios (impersonation restore, god mode
  // marker, impersonation handshake). Mesmo comportamento, apenas isolados em
  // hooks dedicados pra reduzir o tamanho deste arquivo e facilitar leitura.
  useImpersonationRestore(setImpersStash)
  useGodModeMarker(setImpersStash)
  useImpersonationHandshake(setImpersStash)

  useEffect(() => {
    let mounted = true

    // CRÍTICO: o callback do onAuthStateChange roda DENTRO do auth lock do
    // Supabase. Qualquer `await supabase.from(...)` aqui deadlocka — o query
    // tenta adquirir o mesmo lock pra anexar o Authorization header e trava.
    // Solução canônica: callback síncrono + setTimeout(0) pra deferir o
    // trabalho async pro próximo tick do event loop, fora do lock.
    //
    // onAuthStateChange já dispara INITIAL_SESSION ao registrar, então não
    // precisamos chamar getSession() separado — evita corrida entre os dois.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return
      setSession(session)
      setUser(session?.user ?? null)
      setTimeout(() => {
        if (!mounted) return
        loadProfile(session?.user ?? null).finally(() => {
          if (mounted) setLoading(false)
        })
      }, 0)
    })
    return () => { mounted = false; subscription.unsubscribe() }
  }, [loadProfile])

  // ---- Actions ----------------------------------------------------------

  const signInWithGoogle = doSignInWithGoogle

  const signOut = useCallback(async () => {
    await signOutFully(() => {
      setProfile(null)
      setImpersStash(null)
    })
  }, [])

  const setPersona = useCallback((role: PersonaRole) => {
    setPersonaState(PERSONAS[role])
    localStorage.setItem(IMPERS_LS_PERSONA_KEY, role)
  }, [])

  const refetchProfile = useCallback(async () => { await loadProfile(user) }, [loadProfile, user])

  const startImpersonation = useCallback(async (targetUserId: string) => {
    if (!profile) throw new Error('Profile ausente')
    if (!session) throw new Error('Sessão ausente')
    await doStartImpersonation({ targetUserId, profile, session })
  }, [profile, session])

  const endImpersonation = useCallback(async () => {
    await doEndImpersonation(() => setImpersStash(null))
  }, [])

  const enterAdminTenant = useCallback(async (tenantId: string) => {
    if (!profile) throw new Error('Profile ausente')
    await doEnterAdminTenant({ tenantId, profile, setImpersStash })
  }, [profile])

  // ---- Derived state ----------------------------------------------------

  const hasFeature = (key: FeaturePermissionKey): boolean => {
    if (!profile) return false
    return profile.permissions[key] === true
  }

  const resolvedUserName = resolveUserName(user)
  const displayName = profile ? profile.user.name
                    : resolvedUserName || persona.name
  const firstName = displayName.split(' ')[0]

  const effectiveRole: Role | null =
    profile?.team?.role || (profile?.user.user_type === 'client_user' ? 'client' : null)

  const isRealAdmin = profile?.team?.role === 'admin'
  const isPlatformAdmin = !!profile?.is_platform_admin && !impersStash

  const realProfile: CurrentUserProfile | null = impersStash && profile
    ? ({
        ...profile,
        user: { ...profile.user, id: impersStash.admin_user_id, name: impersStash.admin_name, email: impersStash.admin_email },
        is_platform_admin: true,
      } as CurrentUserProfile)
    : profile

  // IMPORTANTE: basear somente em impersStash (não em profile). Durante load,
  // refresh de token, ou qualquer transição onde profile fique null por um
  // instante, `impersonating` não pode virar null — isso faz PlatformAdminGate
  // cair no branch `!is_platform_admin` e mostrar "Sem acesso" por alguns ms,
  // o que confunde e pode até substituir o modal "Encerrar" antes do usuário
  // clicar. Stash é a fonte da verdade de "está impersonando".
  const impersonating: ImpersonatingInfo | null = impersStash ? {
    adminName: impersStash.admin_name,
    adminEmail: impersStash.admin_email,
    targetName: profile?.user.name ?? '—',
    targetTenantName: profile?.tenant?.name ?? '—',
    startedAt: impersStash.started_at,
  } : null

  return (
    <AuthContext.Provider value={{
      user, session, loading, persona, profile, realProfile, access,
      displayName, firstName, effectiveRole, isRealAdmin, isPlatformAdmin, impersonating,
      hasFeature, setPersona, signInWithGoogle, signOut, refetchProfile,
      startImpersonation, endImpersonation, enterAdminTenant,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider')
  return context
}
